/* Nivezzo — product page conversion fixes (2026-09-20)
 *
 * 1. Sticky buy bar on mobile. The only way to buy is the Printify
 *    "Personalize it" button, which sits ~1.2 screens below the fold on a
 *    phone. Ad traffic is mostly mobile, so most visitors never see it.
 * 2. Loading state over the personalizer. Its iframe document answers in
 *    ~80ms but the app inside takes seconds to paint on a first visit,
 *    showing a blank white panel. First-time ad visitors always get that.
 *
 * Written to fail quietly: if any element is missing, nothing is added and
 * the page behaves exactly as before.
 */
(function () {
  'use strict';

  var PERSONALIZER_ORIGIN = 'https://personalize.at';
  var MOBILE_MAX = 749;

  function personalized(form) {
    return !!(form && form.querySelector('input[name="properties[Personalization ID]"]'));
  }

  /* ------------------------------------------------------------------
   * 1. Sticky mobile buy bar
   * ---------------------------------------------------------------- */
  function initStickyBar() {
    var info = document.querySelector('.product__info-container');
    if (!info) return;

    var anchor = info.querySelector('.personalize-container') || info.querySelector('.personalize-button');
    var form = info.querySelector('form[data-type="add-to-cart-form"]');
    var priceEl = info.querySelector('.price__container .price-item--sale, .price__container .price-item--regular, .price .price-item');
    if (!anchor || !form || !priceEl) return;

    var titleEl = info.querySelector('.product__title h1, .product__title');

    var bar = document.createElement('div');
    bar.className = 'nv-stickybar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Buy this product');

    var infoWrap = document.createElement('div');
    infoWrap.className = 'nv-stickybar__info';

    var name = document.createElement('span');
    name.className = 'nv-stickybar__name';
    name.textContent = (titleEl ? titleEl.textContent : '').trim().split(',')[0] || 'This print';

    var price = document.createElement('span');
    price.className = 'nv-stickybar__price';
    price.textContent = priceEl.textContent.trim();

    infoWrap.appendChild(name);
    infoWrap.appendChild(price);

    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'nv-stickybar__cta';

    bar.appendChild(infoWrap);
    bar.appendChild(cta);
    document.body.appendChild(bar);
    document.body.classList.add('nv-has-stickybar');

    function syncLabel() {
      cta.textContent = personalized(form) ? 'Add to cart' : 'Personalize it';
    }

    function syncPrice() {
      var current = info.querySelector('.price__container .price-item--sale, .price__container .price-item--regular, .price .price-item');
      if (current) price.textContent = current.textContent.trim();
    }

    cta.addEventListener('click', function () {
      if (personalized(form)) {
        var submit = form.querySelector('[name="add"]');
        if (submit) submit.click();
        return;
      }
      var button = info.querySelector('.personalize-button');
      if (button) button.click();
    });

    // Show the bar only once the real button has scrolled out of view, so the
    // two never compete for attention.
    //
    // This is deliberately a scroll listener rather than an
    // IntersectionObserver: IO callbacks are tied to the rendering pipeline
    // and go silent whenever the page is not being painted (background tab,
    // minimised window), which also makes the behaviour untestable in a
    // headless pane. A rAF-throttled passive scroll handler costs nothing and
    // always fires.
    // Throttled with a timer rather than requestAnimationFrame for the same
    // reason: rAF is also tied to painting and stalls on an unpainted page.
    var ticking = false;

    function update() {
      ticking = false;
      var pastButton = anchor.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-visible', pastButton && window.innerWidth <= MOBILE_MAX);
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      setTimeout(update, 50);
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('orientationchange', requestUpdate);
    update();

    new MutationObserver(function () {
      syncLabel();
      syncPrice();
    }).observe(form, { childList: true, subtree: true });

    // Variant changes repaint the price outside the form.
    new MutationObserver(syncPrice).observe(info, { childList: true, subtree: true });

    syncLabel();
  }

  /* ------------------------------------------------------------------
   * 2. Loading state over the personalizer iframe
   * ---------------------------------------------------------------- */
  var overlays = new WeakSet();

  function attachOverlay(iframe) {
    if (overlays.has(iframe)) return;
    overlays.add(iframe);

    var host = iframe.parentElement;
    if (!host) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var overlay = document.createElement('div');
    overlay.className = 'nv-pzn-loading';
    overlay.innerHTML =
      '<div class="nv-pzn-loading__spinner" aria-hidden="true"></div>' +
      '<p class="nv-pzn-loading__title">Preparing your live preview…</p>' +
      '<p class="nv-pzn-loading__sub">Type a name and a year, and you will see the finished print before you pay.</p>';
    overlay.setAttribute('role', 'status');
    host.appendChild(overlay);

    var done = false;
    function dismiss() {
      if (done) return;
      done = true;
      overlay.classList.add('is-gone');
      setTimeout(function () {
        if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
      }, 300);
    }

    // The personalizer posts messages once its app is running; that is the
    // earliest honest signal that something is on screen.
    function onMessage(event) {
      if (event.origin !== PERSONALIZER_ORIGIN) return;
      window.removeEventListener('message', onMessage);
      dismiss();
    }
    window.addEventListener('message', onMessage);

    // Fallbacks: the document load event plus a grace period for the app to
    // paint, and a hard cap so the overlay can never trap the customer.
    iframe.addEventListener('load', function () {
      setTimeout(dismiss, 1800);
    });
    setTimeout(dismiss, 15000);
  }

  function watchForPersonalizer() {
    function scan() {
      var frames = document.querySelectorAll('iframe[src*="personalize.at"]');
      for (var i = 0; i < frames.length; i++) attachOverlay(frames[i]);
    }
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    try {
      initStickyBar();
    } catch (e) {
      /* never block the page */
    }
    try {
      watchForPersonalizer();
    } catch (e) {
      /* never block the page */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
